import React from 'react';
import { Loader2, Save, DollarSign } from 'lucide-react';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Textarea } from '../../../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../ui/dialog';
import { SwitchToggle } from './SwitchToggle';
import { StockStatusToggles } from './StockStatusToggles';
import { tierLabels } from '../../../../lib/fabricLabels';

export const MaterialDialog = ({ open, onOpenChange, material, setMaterial, onSave, saving, brandName }) => {
  const L = tierLabels(brandName);
  const set = (key) => (e) => setMaterial(prev => ({ ...prev, [key]: e.target.value }));
  const m = material || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="material-dialog">
        <DialogHeader>
          <DialogTitle>{m.id ? `Edit ${L.tier3}` : `Add New ${L.tier3}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
          <div>
            <Label>{L.tier3} Name *</Label>
            <Input value={m.name || ''} onChange={set('name')} placeholder="e.g., Aspen, Basic, Berlin" className="mt-1" data-testid="material-name-input" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={m.description || ''} onChange={set('description')} placeholder="e.g., Classic blackout fabric with soft texture" className="mt-1" rows={2} />
          </div>
          <div>
            <Label>Preview Image URL</Label>
            <Input value={m.image_url || ''} onChange={set('image_url')} placeholder="https://..." className="mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Composition</Label>
              <Input value={m.composition || ''} onChange={set('composition')} placeholder="e.g., 100% Polyester" className="mt-1" />
            </div>
            <div>
              <Label>Weight</Label>
              <Input value={m.weight || ''} onChange={set('weight')} placeholder="e.g., 280 g/m²" className="mt-1" />
            </div>
          </div>
          <div>
            <Label>Base Price (per sq ft)</Label>
            <div className="relative mt-1">
              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                type="number" step="0.01" value={m.base_price || ''} placeholder="0.00" className="pl-9" data-testid="material-price-input"
                onChange={(e) => setMaterial(prev => ({ ...prev, base_price: parseFloat(e.target.value) || null }))}
              />
            </div>
          </div>
          <div className="border-t pt-4 space-y-3">
            <SwitchToggle
              label="Visible Online" hint="Show in frontend configurator"
              checked={m.is_visible_online !== false}
              onToggle={() => setMaterial(prev => ({ ...prev, is_visible_online: !prev?.is_visible_online }))}
              testId="material-visible-toggle"
            />
            <StockStatusToggles
              value={m.stock_status || null}
              onChange={(v) => setMaterial(prev => ({ ...prev, stock_status: v }))}
              discontinued={!!m.discontinued}
              onDiscontinuedChange={(v) => setMaterial(prev => ({ ...prev, discontinued: v }))}
              testIdPrefix="material"
              hint={(o) => `Applies to every color in this ${L.tier3.toLowerCase()} — ${o.label.toLowerCase()}, text on hover`}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="material-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save {L.tier3}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MaterialDialog;
